import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { VisibilityOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { useControlPreviewMutation } from '@/state/mutations/player/use-control-preview';
import { PreviewRenderer } from '@/components/preview/preview-renderer';
import type { UpdateEventSettingsForm } from '@/schemas/event/update-event-settings';
import type { GetEventSettingsDto } from '@/types/dtos/events/response/get-event-settings.dto';

type CustomizeStickyPreviewProps = {
  eventId?: string;
  formValues: UpdateEventSettingsForm;
  initialSettings?: GetEventSettingsDto;
  logoPreviewUrl?: string | null;
  backgroundPreviewUrl?: string | null;
  onFullscreenPreview?: () => void;
  isFullscreenPreviewPending?: boolean;
};

function patchLogoInPlayerSyncData(
  playerSyncData: Record<string, unknown>,
  nextLogoUrl: string | undefined,
  previousLogoUrl?: string,
): Record<string, unknown> {
  const settings = (playerSyncData.settings as Record<string, unknown> | undefined) ?? {};
  const nextSettings = { ...settings, eventLogoImageUrl: nextLogoUrl };

  const layers = playerSyncData.layers;
  if (!Array.isArray(layers) || (!previousLogoUrl && !nextLogoUrl)) {
    return { ...playerSyncData, settings: nextSettings };
  }

  const patchedLayers = layers.map((layer) => {
    if (!layer || typeof layer !== 'object') return layer;
    const data = (layer as { data?: Record<string, string> }).data;
    if (!data) return layer;
    const nextData = { ...data };
    let changed = false;
    for (const [key, value] of Object.entries(nextData)) {
      if (typeof value !== 'string') continue;
      if (previousLogoUrl && value.includes(previousLogoUrl)) {
        nextData[key] = nextLogoUrl ? value.split(previousLogoUrl).join(nextLogoUrl) : '';
        changed = true;
      }
    }
    return changed ? { ...layer, data: nextData } : layer;
  });

  return { ...playerSyncData, settings: nextSettings, layers: patchedLayers };
}

function patchBackgroundLayers(
  playerSyncData: Record<string, unknown>,
  backgroundType: 'color' | 'image' | undefined,
  backgroundColor: string | undefined,
  backgroundImageUrl: string | undefined,
  overlay?: {
    enabled?: boolean;
    color?: string;
    transparency?: number;
  },
): Record<string, unknown> {
  const layers = playerSyncData.layers;
  if (!Array.isArray(layers)) return playerSyncData;

  const patchedLayers = layers.map((layer) => {
    if (!layer || typeof layer !== 'object') return layer;
    const componentId = (layer as { componentId?: string }).componentId;
    if (componentId !== 'background' && componentId !== 'backgroundVideo') return layer;

    const data = { ...((layer as { data?: Record<string, string> }).data ?? {}) };
    if (backgroundType === 'image') {
      if (backgroundImageUrl) {
        data.imageUrl = backgroundImageUrl;
        data.fit = data.fit || 'cover';
      } else {
        delete data.imageUrl;
      }
      if (backgroundColor) data.backgroundColor = backgroundColor;
    } else {
      delete data.imageUrl;
      if (backgroundColor) data.backgroundColor = backgroundColor;
    }

    if (overlay?.enabled && overlay.color) {
      data.overlayEnabled = 'true';
      data.overlayColor = overlay.color;
      if (overlay.transparency != null) {
        data.overlayTransparency = String(overlay.transparency);
      } else {
        delete data.overlayTransparency;
      }
    } else {
      delete data.overlayEnabled;
      delete data.overlayColor;
      delete data.overlayTransparency;
    }

    return { ...layer, data };
  });

  return { ...playerSyncData, layers: patchedLayers };
}

function replaceColorOccurrences(value: string, fromColor: string, toColor: string): string {
  if (!fromColor || !toColor || fromColor.toLowerCase() === toColor.toLowerCase()) return value;
  const escaped = fromColor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return value.replace(new RegExp(escaped, 'gi'), toColor);
}

function patchTextStyles(
  playerSyncData: Record<string, unknown>,
  nextTextColor: string | undefined,
  previousTextColor: string | undefined,
  fontSizeMultiplier: number | undefined,
): Record<string, unknown> {
  const layers = playerSyncData.layers;
  if (!Array.isArray(layers) || !nextTextColor) return playerSyncData;

  const patchedLayers = layers.map((layer) => {
    if (!layer || typeof layer !== 'object') return layer;
    const typed = layer as {
      componentId?: string;
      style?: { textColor?: string; fontSizeMultiplier?: number };
      data?: Record<string, string>;
    };
    if (typed.componentId === 'liveTranslation') return layer;
    const nextStyle = {
      ...(typed.style ?? {}),
      textColor: nextTextColor,
      ...(fontSizeMultiplier != null ? { fontSizeMultiplier } : {}),
    };

    let nextData = typed.data;
    if (nextData && previousTextColor) {
      const replacedEntries = Object.entries(nextData).map(([key, value]) =>
        typeof value === 'string'
          ? [key, replaceColorOccurrences(value, previousTextColor, nextTextColor)]
          : [key, value],
      );
      nextData = Object.fromEntries(replacedEntries) as Record<string, string>;
    }

    return { ...layer, style: nextStyle, ...(nextData ? { data: nextData } : {}) };
  });

  return { ...playerSyncData, layers: patchedLayers };
}

const PREVIEW_TRANSLATION_LAYER_ID = 401;

export function ensureLiveTranslationPreviewLayer(
  playerSyncData: Record<string, unknown>,
  fontSizeMultiplier: number | undefined,
  textColor?: string,
): Record<string, unknown> {
  const layers = Array.isArray(playerSyncData.layers) ? playerSyncData.layers : [];

  let found = false;
  const patchedLayers = layers.map((layer) => {
    if (!layer || typeof layer !== 'object') return layer;
    const typed = layer as {
      componentId?: string;
      style?: { fontSizeMultiplier?: number; textColor?: string };
    };
    if (typed.componentId !== 'liveTranslation') return layer;
    found = true;
    return {
      ...layer,
      style: {
        ...(typed.style ?? {}),
        ...(fontSizeMultiplier != null ? { fontSizeMultiplier } : {}),
        ...(textColor ? { textColor } : {}),
      },
    };
  });

  if (!found) {
    patchedLayers.push({
      layerId: PREVIEW_TRANSLATION_LAYER_ID,
      componentId: 'liveTranslation',
      position: { x: 5, y: 84, w: 90, h: 14 },
      visible: true,
      data: { languages: 'de,en', showLanguageMarker: 'true' },
      style: {
        backgroundColor: 'rgba(0,0,0,0.6)',
        textAlign: 'center',
        maxLines: 2,
        textColor: textColor || '#ffffff',
        ...(fontSizeMultiplier != null ? { fontSizeMultiplier } : {}),
      },
    });
  }

  return { ...playerSyncData, layers: patchedLayers };
}

export function CustomizeStickyPreview({
  eventId,
  formValues,
  initialSettings,
  logoPreviewUrl,
  backgroundPreviewUrl,
  onFullscreenPreview,
  isFullscreenPreviewPending = false,
}: CustomizeStickyPreviewProps) {
  const t = useTranslations();
  const { mutateAsync: controlPreviewMutation, isPending, isError } = useControlPreviewMutation();
  const [baseSyncData, setBaseSyncData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await controlPreviewMutation({
          entityType: 'Event',
          entityId: eventId,
          contextEntityId: eventId,
          contextEntityType: 'Event',
          playType: 'event-info',
        });
        if (!cancelled) {
          setBaseSyncData(response.playerSyncData);
        }
      } catch {
        if (!cancelled) {
          setBaseSyncData(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Fetch once per event; mutateAsync identity is not stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const playerSyncData = useMemo(() => {
    if (!baseSyncData) return null;

    const backgroundImageUrl =
      formValues.backgroundType === 'image'
        ? backgroundPreviewUrl || initialSettings?.backgroundImageUrl || undefined
        : undefined;

    const deleteLogo = formValues.deleteEventLogo === true && !formValues.eventLogoImage;
    const nextLogoUrl = deleteLogo
      ? undefined
      : logoPreviewUrl ||
        (formValues.eventLogoImage ? logoPreviewUrl : initialSettings?.eventLogoImageUrl) ||
        undefined;

    const previousTextColor =
      initialSettings?.textColor ||
      ((baseSyncData.settings as { textColor?: string } | undefined)?.textColor ?? undefined);

    const withLogo = patchLogoInPlayerSyncData(baseSyncData, nextLogoUrl, initialSettings?.eventLogoImageUrl);
    const withBackground = patchBackgroundLayers(
      withLogo,
      formValues.backgroundType,
      formValues.backgroundColor,
      backgroundImageUrl,
      {
        enabled: formValues.backgroundOverlay,
        color: formValues.backgroundOverlayColor,
        transparency: formValues.backgroundOverlayTransparency,
      },
    );
    const withText = patchTextStyles(
      withBackground,
      formValues.textColor,
      previousTextColor,
      formValues.fontSizeMultiplier,
    );
    const withCaptions = ensureLiveTranslationPreviewLayer(
      withText,
      formValues.liveTranslationFontSizeMultiplier ?? formValues.fontSizeMultiplier,
      formValues.liveTranslationTextColor,
    );

    const settings = {
      ...((withCaptions.settings as Record<string, unknown> | undefined) ?? {}),
      backgroundType: formValues.backgroundType,
      backgroundColor: formValues.backgroundColor,
      textColor: formValues.textColor,
      backgroundImageUrl,
      eventLogoImageUrl: nextLogoUrl,
      backgroundOverlay: formValues.backgroundOverlay,
      backgroundOverlayColor: formValues.backgroundOverlayColor,
      backgroundOverlayTransparency: formValues.backgroundOverlayTransparency,
      fontSizeMultiplier: formValues.fontSizeMultiplier,
      liveTranslationFontSizeMultiplier: formValues.liveTranslationFontSizeMultiplier ?? formValues.fontSizeMultiplier,
      liveTranslationTextColor: formValues.liveTranslationTextColor,
      margins: formValues.margins,
    };

    return {
      ...withCaptions,
      settings,
    };
  }, [baseSyncData, formValues, initialSettings, logoPreviewUrl, backgroundPreviewUrl]);

  // Remount when visual settings change so layer children always pick up the new snapshot.
  const previewKey = useMemo(() => {
    if (!playerSyncData) return 'empty';
    const s = playerSyncData.settings as Record<string, unknown>;
    return [
      s.backgroundType,
      s.backgroundColor,
      s.backgroundImageUrl,
      s.textColor,
      s.backgroundOverlay,
      s.backgroundOverlayColor,
      s.backgroundOverlayTransparency,
      s.fontSizeMultiplier,
      s.liveTranslationFontSizeMultiplier,
      s.liveTranslationTextColor,
      s.eventLogoImageUrl,
    ].join('|');
  }, [playerSyncData]);

  return (
    <Stack spacing={1.5} sx={{ height: '100%' }}>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2">{t('event.detail.settingsMenu.customize.livePreview')}</Typography>
          {onFullscreenPreview && (
            <Button
              onClick={onFullscreenPreview}
              variant="outlined"
              size="small"
              startIcon={
                isFullscreenPreviewPending ? <CircularProgress size={16} /> : <VisibilityOutlined fontSize="small" />
              }
              disabled={isFullscreenPreviewPending}
              sx={{ height: 'auto', minHeight: 32, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {t('event.detail.settingsMenu.customize.preview')}
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('event.detail.settingsMenu.customize.livePreviewHint')}
        </Typography>
      </Box>
      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          overflow: 'hidden',
          bgcolor: 'grey.100',
        }}
      >
        {isPending && !playerSyncData && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary">
              {t('event.detail.settingsMenu.customize.previewLoading')}
            </Typography>
          </Stack>
        )}
        {isError && !playerSyncData && (
          <Box sx={{ p: 2 }}>
            <Alert severity="warning">{t('event.detail.settingsMenu.customize.previewError')}</Alert>
          </Box>
        )}
        {playerSyncData && (
          <Box key={previewKey} sx={{ width: '100%', height: '100%' }}>
            <PreviewRenderer playerSyncData={playerSyncData} forceHtmlTextColor />
          </Box>
        )}
      </Paper>
    </Stack>
  );
}
